import React, { useState, useEffect, useRef } from "react";
import { Box, TextField, CircularProgress, Alert } from "@mui/material";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const C = {
  bg: '#FAFAF9', surface: '#FFFFFF', surface2: '#F6F6F4',
  ink1: '#0B1020', ink2: '#1F2937', ink3: '#475569', ink4: '#64748B', ink5: '#94A3B8',
  line: '#E7E5E4', line2: '#EFEDEA',
  indigo: '#4F46E5', indigo50: '#EEF2FF',
  emerald: '#059669', rose: '#E11D48',
  shadowSm: '0 1px 2px rgba(15,23,42,.04)',
};

const f = {
  display: "'Inter Tight','Inter',sans-serif",
  body: "'Inter',system-ui,sans-serif",
  mono: "'JetBrains Mono',ui-monospace,monospace",
};

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    ['link'], ['clean']
  ],
};

const quillModulesReadonly = { toolbar: false };

function Btn({ primary, danger, ghost, onClick, disabled, children }) {
  const bg = primary ? C.indigo : danger ? C.rose : ghost ? 'none' : C.surface;
  const border = primary ? C.indigo : danger ? C.rose : ghost ? 'none' : C.line;
  const color = primary || danger ? '#fff' : ghost ? C.ink4 : C.ink2;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      height: 32, padding: '0 13px', borderRadius: 7, border: `1px solid ${border}`,
      background: disabled ? C.surface2 : bg, color: disabled ? C.ink5 : color,
      cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12,
      fontFamily: f.body, fontWeight: 500, transition: '.12s', display: 'inline-flex', alignItems: 'center', gap: 6,
    }}>{children}</button>
  );
}

function VersionsTab({ apiBase, token, userRole }) {
  const [applications, setApplications] = useState([]);
  const [versionsByApp, setVersionsByApp] = useState({});
  const [expandedApps, setExpandedApps] = useState({});
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [mode, setMode] = useState('view');
  const [formVersion, setFormVersion] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [appLoading, setAppLoading] = useState(false);
  const quillRef = useRef(null);

  useEffect(() => {
    if (!apiBase || !token) return;
    setAppLoading(true);
    fetch(`${apiBase}/applications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(apps => setApplications(Array.isArray(apps) ? apps : []))
      .catch(() => setError('Failed to fetch applications'))
      .finally(() => setAppLoading(false));
  }, [apiBase, token]);

  function fetchVersions(appId) {
    setLoading(true);
    fetch(`${apiBase}/applications/${appId}/versions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(versions => setVersionsByApp(v => ({ ...v, [appId]: Array.isArray(versions) ? versions : [] })))
      .catch(() => setError('Failed to fetch versions'))
      .finally(() => setLoading(false));
  }

  function handleToggleApp(appId) {
    const isExpanded = expandedApps[appId];
    setExpandedApps(e => ({ ...e, [appId]: !isExpanded }));
    if (!isExpanded) { fetchVersions(appId); setSelectedAppId(appId); }
  }

  function handleSelectVersion(version, appId) {
    setSelectedVersion(version);
    setSelectedAppId(appId);
    setMode('view');
    setError('');
    try {
      const data = typeof version.data === 'string' ? JSON.parse(version.data) : (version.data || {});
      setFormMessage(data.message || '');
    } catch { setFormMessage(''); }
  }

  function handleAddVersionStart(appId) {
    setSelectedAppId(appId);
    setSelectedVersion(null);
    setMode('add');
    setFormVersion('');
    setFormMessage('');
    setError('');
  }

  async function handleSaveVersion() {
    setError('');
    setLoading(true);
    try {
      let delta = null;
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        delta = editor.getContents();
      }
      const dataStr = JSON.stringify({ message: formMessage, messageDelta: delta });
      if (mode === 'add') {
        if (!formVersion.trim()) { setError('Version number is required'); setLoading(false); return; }
        const res = await fetch(`${apiBase}/applications/${selectedAppId}/versions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ version: formVersion.trim(), data: dataStr })
        });
        if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed to create version'); }
        fetchVersions(selectedAppId);
        setMode('view');
        setFormVersion('');
        setSelectedVersion(null);
      } else if (mode === 'edit') {
        const res = await fetch(`${apiBase}/versions/${selectedVersion.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ data: dataStr })
        });
        if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed to update version'); }
        fetchVersions(selectedAppId);
        setSelectedVersion(prev => ({ ...prev, data: dataStr }));
        setMode('view');
      }
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  async function handleDeleteVersion(versionId) {
    if (!window.confirm('Delete this version?')) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/versions/${versionId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete version');
      fetchVersions(selectedAppId);
      if (selectedVersion && selectedVersion.id === versionId) { setSelectedVersion(null); setMode('view'); }
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', padding: '18px 20px 60px', background: C.bg, minHeight: '100%' }}>

      {/* page header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11.5, color: C.ink5, marginBottom: 3 }}>
          License Manager &nbsp;/&nbsp; <b style={{ color: C.ink3, fontWeight: 500 }}>Versions</b>
        </div>
        <h1 style={{ fontFamily: f.display, fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.02em', color: C.ink1 }}>
          App Versions
        </h1>
      </div>

      <div style={{ display: 'flex', gap: 12, minHeight: '70vh' }}>

        {/* sidebar */}
        <div style={{ width: 260, background: C.surface, border: `1px solid ${C.line}`, borderRadius: 10, boxShadow: C.shadowSm, padding: '14px 12px', overflowY: 'auto', flexShrink: 0 }}>
          <div style={{ fontFamily: f.display, fontWeight: 600, fontSize: 13, color: C.ink1, marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${C.line2}` }}>
            Applications
          </div>
          {appLoading ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <CircularProgress size={20} />
            </div>
          ) : (
            applications.map(app => (
              <div key={app.id} style={{ marginBottom: 2 }}>
                <div
                  onClick={() => handleToggleApp(app.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    padding: '7px 8px', borderRadius: 6, fontSize: 12.5, fontWeight: 600,
                    color: expandedApps[app.id] ? C.indigo : C.ink2,
                    background: expandedApps[app.id] ? C.indigo50 : 'none',
                    transition: '.12s',
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style={{ transform: expandedApps[app.id] ? 'rotate(90deg)' : 'none', transition: '.12s', flexShrink: 0 }}>
                    <path d="M8 5l8 7-8 7z"/>
                  </svg>
                  <span style={{ fontFamily: f.body }}>{app.name}</span>
                </div>
                {expandedApps[app.id] && (
                  <div style={{ paddingLeft: 20, paddingTop: 4 }}>
                    {userRole === 'admin' && (
                      <button onClick={() => handleAddVersionStart(app.id)} style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '5px 8px',
                        borderRadius: 5, border: 0, background: 'none', cursor: 'pointer',
                        fontSize: 11.5, color: C.indigo, fontFamily: f.body, fontWeight: 500, marginBottom: 4,
                      }}>
                        + Add Version
                      </button>
                    )}
                    {loading && !versionsByApp[app.id] ? (
                      <CircularProgress size={14} />
                    ) : (
                      (versionsByApp[app.id] || []).map(v => (
                        <div
                          key={v.id}
                          onClick={() => handleSelectVersion(v, app.id)}
                          style={{
                            padding: '5px 8px 5px 10px', cursor: 'pointer', borderRadius: 5,
                            fontSize: 12, fontFamily: f.mono,
                            background: selectedVersion?.id === v.id ? C.indigo50 : 'none',
                            color: selectedVersion?.id === v.id ? C.indigo : C.ink3,
                            borderLeft: `2px solid ${selectedVersion?.id === v.id ? C.indigo : 'transparent'}`,
                            marginBottom: 2, transition: '.1s',
                          }}
                        >
                          {v.version}
                        </div>
                      ))
                    )}
                    {!loading && (versionsByApp[app.id] || []).length === 0 && (
                      <div style={{ fontSize: 11.5, color: C.ink5, paddingLeft: 8, paddingBottom: 4 }}>No versions yet</div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
          {!appLoading && applications.length === 0 && (
            <div style={{ fontSize: 12, color: C.ink5, padding: '8px 4px' }}>No applications found.</div>
          )}
        </div>

        {/* content area */}
        <div style={{ flex: 1, background: C.surface, border: `1px solid ${C.line}`, borderRadius: 10, boxShadow: C.shadowSm, padding: '20px 24px', display: 'flex', flexDirection: 'column' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
          )}

          {!selectedVersion && mode !== 'add' && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <div style={{ textAlign: 'center', color: C.ink5 }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: 10 }}><path d="M12 2v20"/><path d="m5 9 7-7 7 7"/><path d="M5 15h14"/></svg>
                <div style={{ fontSize: 13, fontFamily: f.display, fontWeight: 500 }}>Select a version to view its details</div>
              </div>
            </div>
          )}

          {mode === 'add' && (
            <div>
              <h2 style={{ fontFamily: f.display, fontWeight: 600, fontSize: 16, color: C.ink1, margin: '0 0 16px' }}>Add New Version</h2>
              <TextField label="Version Number" value={formVersion} onChange={e => setFormVersion(e.target.value)}
                sx={{ mb: 2, width: 200 }} placeholder="e.g. 1.0.0" size="small" />
              <div style={{ fontSize: 11.5, fontWeight: 600, color: C.ink4, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Release Notes</div>
              <Box sx={{ mb: 8 }}>
                <ReactQuill ref={quillRef} value={formMessage} onChange={setFormMessage} modules={quillModules} theme="snow" style={{ minHeight: 250 }} />
              </Box>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Btn primary onClick={handleSaveVersion} disabled={loading}>{loading ? 'Saving…' : 'Save Version'}</Btn>
                <Btn ghost onClick={() => { setMode('view'); setError(''); setSelectedVersion(null); }}>Cancel</Btn>
              </div>
            </div>
          )}

          {selectedVersion && mode === 'view' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <h2 style={{ fontFamily: f.display, fontWeight: 600, fontSize: 18, color: C.ink1, margin: 0 }}>
                    Version {selectedVersion.version}
                  </h2>
                  <div style={{ fontFamily: f.mono, fontSize: 11, color: C.ink5, marginTop: 4 }}>
                    Created: {selectedVersion.createdAt?.slice?.(0, 19).replace('T', ' ')}
                  </div>
                </div>
                {userRole === 'admin' && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Btn onClick={() => setMode('edit')}>Edit</Btn>
                    <Btn danger onClick={() => handleDeleteVersion(selectedVersion.id)}>Delete</Btn>
                  </div>
                )}
              </div>
              <div style={{ borderTop: `1px solid ${C.line}`, paddingTop: 16 }}>
                <ReactQuill value={formMessage} readOnly={true} modules={quillModulesReadonly} theme="snow" />
              </div>
            </div>
          )}

          {selectedVersion && mode === 'edit' && (
            <div>
              <h2 style={{ fontFamily: f.display, fontWeight: 600, fontSize: 16, color: C.ink1, margin: '0 0 4px' }}>
                Edit Version {selectedVersion.version}
              </h2>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: C.ink4, textTransform: 'uppercase', letterSpacing: '.05em', margin: '12px 0 8px' }}>Release Notes</div>
              <Box sx={{ mb: 8 }}>
                <ReactQuill ref={quillRef} value={formMessage} onChange={setFormMessage} modules={quillModules} theme="snow" style={{ minHeight: 250 }} />
              </Box>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Btn primary onClick={handleSaveVersion} disabled={loading}>{loading ? 'Saving…' : 'Save'}</Btn>
                <Btn ghost onClick={() => { setMode('view'); setError(''); }}>Cancel</Btn>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default VersionsTab;
